import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import ClinicCard from "@/components/ClinicCard";
import NationalContextSidebar from "@/components/NationalContextSidebar";
import Rubric from "@/components/Rubric";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { VALENCIA_CLINICS } from "@/data/clinics-valencia";
import { Helmet } from "react-helmet-async";

const PATH = "/best-veneer-clinics-in-valencia";
const TITLE = "Porcelain veneer clinics in Valencia — an evaluation";
const DESCRIPTION =
  "Five Valencia cosmetic-dentistry practices examined on the same twelve-dimension rubric. Each clinic linked to its own website.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestVeneerClinicsValencia() {
  const itemList = itemListSchema(
    VALENCIA_CLINICS.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
    PATH,
  );

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Spain", path: "/best-smile-makeover-clinics-in-spain" },
          { name: "Valencia", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Comparison &middot; Valencia"
        title="Porcelain veneer clinics in Valencia — an evaluation"
        standfirst="Five Valencia cosmetic-dentistry practices examined on a single rubric, in the same order, linked to their own websites. Valencia contains one of Spain's cleanest examples of a studio positioned specifically around porcelain veneers — and three meaningfully different alternative models alongside it."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={15}
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>Why Valencia, as a category</h2>
        <p>
          Valencia is smaller than Madrid or Barcelona as a cosmetic-
          dentistry market, which produces an interesting effect: the
          practices that specialise narrowly tend to specialise very
          narrowly indeed, and the generalist multi-disciplinary
          practices make fewer claims to aesthetic primacy. The city
          also has a distinct orthodontics-first cohort — clinics that
          treat alignment as the first move, then evaluate whether
          minimally-invasive veneer work is warranted at all.
        </p>
        <p>
          This piece examines five practices across that range,
          evaluated on the same rubric.
        </p>

        <h2>The rubric, applied locally</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          For Valencia specifically, the dimensions that differentiate
          most sharply are single-clinician versus multi-clinician
          structure, material-specific veneer positioning (one clinic
          on this list anchors its public material to a specific
          ceramic system and thickness), and hospital-adjacency for
          more complex cases.
        </p>

        <h2>The clinics evaluated</h2>
        <p>
          Profiles below follow the same structure — short description,
          dimension-by-dimension snapshot, and an honest statement of
          where the clinic leads and trails on this list. Every
          external link goes to the clinic's own website.
        </p>

        {VALENCIA_CLINICS.map((clinic, i) => (
          <ClinicCard key={clinic.slug} clinic={clinic} position={i + 1} />
        ))}

        <h2>What the framework surfaces, on balance</h2>
        <p>
          On <strong>narrow specialisation</strong>, Valencia contains
          a single-clinician veneer studio whose public material treats
          porcelain laminate veneers as the defining service rather
          than a menu item. Readers whose case is aesthetic-only and
          whose short list is for a designed smile — not a general
          dental relationship — will find this model the cleanest fit
          on the list.
        </p>
        <p>
          On <strong>medical-grade infrastructure</strong>, the
          hospital-adjacent practice leads. Cases with surgical or
          complex-prosthetic components are better served inside a
          medical footprint than inside a single-clinician studio.
        </p>
        <p>
          On <strong>orthodontics-first sequencing</strong>, a high-
          volume invisible-orthodontics practice on this list
          represents a distinct philosophy: align first, reduce
          aesthetic intervention second. Readers whose case might
          realistically be solved with alignment alone should take
          this model seriously before committing to a veneer plan.
        </p>

        <NationalContextSidebar currentCity="Valencia" />

        <h2>Related reading</h2>
        <p>
          Readers comparing Valencia clinics to clinics in other
          Spanish cities should consult the{" "}
          <Link to="/best-smile-makeover-clinics-in-spain">
            national smile-makeover guide
          </Link>
          . If the shortlist also includes Barcelona, see the{" "}
          <Link to="/best-veneer-clinics-in-barcelona">
            Barcelona evaluation
          </Link>
          ; if Seville,{" "}
          <Link to="/best-veneer-clinics-in-seville">
            the Seville evaluation
          </Link>
          . The vocabulary used in the profiles above is developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          .
        </p>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Each clinic's name, site
              and public positioning confirmed against the clinic's own
              website at the time of writing.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
